/**
 * Admin — the cycle planning page (#1522).
 *
 * One page runs the planning meeting: where we are, the cycle goal, scope,
 * decisions, requests, ideas to try, and the wrap-up. The draft comes from
 * the project board on GitHub by itself (a curated file can still be imported
 * over it); every change in the room is saved for everyone, and the page
 * refreshes every few seconds. Present shows it one section at a time, for
 * sharing a screen (PlanningPresent).
 */

import { useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBar,
  MessageBarBody,
  OverlayDrawer,
  Select,
  Spinner,
  Title2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  Add20Regular,
  ArrowSync20Regular,
  ArrowUpload20Regular,
  CalendarLtr20Regular,
  CalendarLtr24Regular,
  Dismiss20Regular,
  Gavel20Regular,
  Presenter20Regular,
} from '@fluentui/react-icons'
import { ErrorCard } from '../components/ErrorCard'
import { useToast } from '../components/ToastProvider'
import { DecisionRail } from '../components/planning/DecisionRail'
import { PlanningStatus } from '../components/planning/PlanningStatus'
import {
  InfoHint,
  PlanSection,
  useAgendaStyles,
  usePanelStyles,
} from '../components/planning/PlanParts'
import { spotlight, usePlanSections } from '../components/planning/planSections'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import {
  useBoardCycles,
  useImportPlan,
  useRefreshPlan,
  usePlanningCycle,
  usePlanningCycles,
  useStartCycle,
} from '../hooks/usePlanning'
import { apiErrorMessage } from '../lib/apiError'
import { boardCycleDates, decisionLog } from '../lib/planning'
import { formatRelative } from '../lib/relativeTime'
import { contentBg } from '../theme'
import type { Plan, PlanningCycle } from '../types/planning'

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXL,
    maxWidth: '1200px',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  titles: { display: 'flex', flexDirection: 'column', gap: '2px' },
  tools: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  // The agenda sticks to the top of the page, so the marked item stays in
  // view wherever the meeting has scrolled to.
  agendaBar: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    padding: `${tokens.spacingVerticalS} 0`,
    background: contentBg,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  empty: { alignItems: 'flex-start', maxWidth: '560px' },
  count: { marginLeft: tokens.spacingHorizontalS },
})

export function AdminPlanning() {
  const styles = useStyles()
  const panel = usePanelStyles()
  const { showToast } = useToast()
  const [params, setParams] = useSearchParams()
  const cycles = usePlanningCycles()
  const chosen = params.get('cycle') || cycles.data?.[0]?.cycle
  const current = usePlanningCycle(chosen)
  const importPlan = useImportPlan()
  const refresh = useRefreshPlan(chosen ?? '')
  const startCycle = useStartCycle()
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmRefresh, setConfirmRefresh] = useState(false)
  const boardCycles = useBoardCycles(menuOpen)
  const fileRef = useRef<HTMLInputElement>(null)
  const [railOpen, setRailOpen] = useState(false)
  const navigate = useNavigate()
  useDocumentTitle(current.data?.plan.title?.replace(/\s*·\s*/g, ' ') || 'Planning')

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    let plan: Plan
    try {
      plan = JSON.parse(await file.text())
    } catch {
      showToast({ title: 'Not a plan file', body: `${file.name} is not valid JSON.`, intent: 'error' })
      return
    }
    if (!plan || typeof plan.cycle !== 'string' || !plan.cycle) {
      showToast({
        title: 'Not a plan file',
        body: `${file.name} has no cycle code. Build it with plan.py gather.`,
        intent: 'error',
      })
      return
    }
    try {
      const saved = await importPlan.mutateAsync(plan)
      setParams({ cycle: saved.cycle })
      showToast({ title: `Imported ${saved.cycle}`, intent: 'success' })
    } catch (err) {
      showToast({ title: 'Could not import', body: apiErrorMessage(err), intent: 'error' })
    }
  }

  const importButton = (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        hidden
        onChange={onFile}
        data-testid="plan-file"
      />
      <Button
        icon={<ArrowUpload20Regular />}
        disabled={importPlan.isPending}
        onClick={() => fileRef.current?.click()}
      >
        {importPlan.isPending ? 'Importing…' : 'Import plan'}
      </Button>
    </>
  )
  const importHint = (
    <InfoHint text="The current cycle comes from the GitHub board. New cycle opens a later one. Import a plan file (plan.py gather) to use it instead; importing again keeps the room's changes." />
  )

  const onRefresh = async () => {
    try {
      await refresh.mutateAsync()
      showToast({ title: `${chosen} read from GitHub`, intent: 'success' })
    } catch (err) {
      showToast({ title: 'Could not refresh', body: apiErrorMessage(err), intent: 'error' })
    }
  }

  const onStart = async (cycle: string, start: string) => {
    try {
      const opened = await startCycle.mutateAsync({ cycle, start })
      setParams({ cycle: opened.cycle })
      showToast({ title: `${opened.cycle} read from GitHub`, intent: 'success' })
    } catch (err) {
      showToast({ title: `Could not open ${cycle}`, body: apiErrorMessage(err), intent: 'error' })
    }
  }
  // Refresh swaps an imported file for the GitHub draft, which has no requests
  // and one card per issue, so it asks first (planning review, #1578).
  const imported = !!current.data && current.data.plan.source !== 'github'
  const today = new Date().toISOString().slice(0, 10)
  // Planning is for a cycle still to come, so ended ones are not offered. The
  // plan list is checked too: the board read can be up to a minute old, and
  // opening a cycle that is already here would replace its imported file.
  // A plan with dates is that cycle only: last year's Q4C1 does not hide this year's.
  const listed = new Set((cycles.data ?? []).map((c) => (c.start ? `${c.cycle}@${c.start}` : c.cycle)))
  const startable = (boardCycles.data ?? []).filter(
    (c) =>
      !c.on_page && !listed.has(c.cycle) && !listed.has(`${c.cycle}@${c.start}`) && c.end > today,
  )
  const newCycleMenu = (
    <Menu open={menuOpen} onOpenChange={(_, d) => setMenuOpen(d.open)}>
      <MenuTrigger disableButtonEnhancement>
        <Button icon={<Add20Regular />} disabled={startCycle.isPending}>
          {startCycle.isPending ? 'Opening…' : 'New cycle'}
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {boardCycles.isLoading ? (
            <MenuItem disabled>Reading the board…</MenuItem>
          ) : boardCycles.error ? (
            <MenuItem disabled>
              {apiErrorMessage(boardCycles.error, 'Could not read the board.')}
            </MenuItem>
          ) : startable.length === 0 ? (
            <MenuItem disabled>Every upcoming cycle is already here</MenuItem>
          ) : (
            startable.map((c) => (
              <MenuItem
                key={`${c.cycle}@${c.start}`}
                icon={<CalendarLtr20Regular />}
                onClick={() => void onStart(c.cycle, c.start)}
              >
                {c.cycle} · {boardCycleDates(c)}
              </MenuItem>
            ))
          )}
        </MenuList>
      </MenuPopover>
    </Menu>
  )

  if (cycles.isLoading) return <Spinner label="Loading…" />
  if (cycles.error) {
    return (
      <ErrorCard
        title="Couldn't load the plans"
        message={apiErrorMessage(cycles.error, 'Please try again.')}
        retry={() => void cycles.refetch()}
      />
    )
  }
  if (!chosen) {
    return (
      <div className={styles.page}>
        <Title2 as="h1">Planning</Title2>
        <div className={mergeClasses(panel.panel, styles.empty)}>
          <div className={panel.panelHead}>
            <CalendarLtr24Regular aria-hidden="true" />
            No plan yet
            {importHint}
          </div>
          <div className={styles.tools}>
            {newCycleMenu}
            {importButton}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.titles}>
          <Title2 as="h1">{current.data?.plan.title || chosen}</Title2>
          {current.data?.plan.subtitle && <Caption1>{current.data.plan.subtitle}</Caption1>}
          {current.data?.plan.source === 'github' && current.data.plan.fetched_at ? (
            <Caption1 data-testid="plan-source">
              From GitHub · read {formatRelative(current.data.plan.fetched_at)}
            </Caption1>
          ) : (
            imported && <Caption1 data-testid="plan-source">Imported file</Caption1>
          )}
        </div>
        <div className={styles.tools}>
          {current.data && (
            <DecisionsButton data={current.data} onClick={() => setRailOpen(true)} />
          )}
          <Button
            appearance="primary"
            icon={<Presenter20Regular />}
            disabled={!current.data}
            onClick={() => {
              // Asked on the click: a browser grants full screen only to a gesture.
              document.documentElement.requestFullscreen?.().catch(() => {})
              navigate(`/admin/planning/present?cycle=${encodeURIComponent(chosen)}`)
            }}
          >
            Present
          </Button>
          {(cycles.data?.length ?? 0) > 1 && (
            <Select
              aria-label="Cycle"
              value={chosen}
              onChange={(_, d) => setParams({ cycle: d.value })}
            >
              {cycles.data!.map((c) => (
                <option key={c.cycle} value={c.cycle}>
                  {c.cycle}
                </option>
              ))}
            </Select>
          )}
          <Button
            icon={<ArrowSync20Regular />}
            // Until the plan is here, nobody knows whether it is an imported file.
            disabled={refresh.isPending || !current.data}
            aria-label="Refresh from GitHub"
            title={
              imported
                ? 'Replace the imported file with the GitHub board.'
                : "Read the cycle from GitHub again. The room's changes stay."
            }
            onClick={() => (imported ? setConfirmRefresh(true) : void onRefresh())}
          >
            {refresh.isPending ? 'Refreshing…' : 'Refresh'}
          </Button>
          {newCycleMenu}
          {importButton}
          {importHint}
        </div>
      </header>
      {current.data && <PlanningStatus data={current.data} />}

      <Dialog open={confirmRefresh} onOpenChange={(_, d) => setConfirmRefresh(d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Replace the imported file?</DialogTitle>
            <DialogContent>
              {chosen} is read from GitHub instead. The file's requests and grouped cards go
              away.
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button>Cancel</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                onClick={() => {
                  setConfirmRefresh(false)
                  void onRefresh()
                }}
              >
                Replace
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {current.isLoading ? (
        <Spinner label="Loading…" />
      ) : !current.data ? (
        <ErrorCard
          title={`Couldn't load ${chosen}`}
          message={apiErrorMessage(current.error, 'Please try again.')}
          retry={() => void current.refetch()}
        />
      ) : (
        <>
          {/* A failed poll keeps the board: swapping it for an error card would
              drop what people are typing (planning review, #1578). */}
          {current.error && (
            <MessageBar intent="warning">
              <MessageBarBody>Connection lost. Retrying…</MessageBarBody>
            </MessageBar>
          )}
          {/* Without Issues access GitHub hides items instead of refusing, so
              a board short of cards would otherwise look like a quiet cycle. */}
          {(current.data?.plan.hidden ?? 0) > 0 && (
            <MessageBar intent="warning" data-testid="plan-hidden">
              <MessageBarBody>
                {current.data?.plan.hidden} board items are left out: the GitHub token can't read
                the repositories they're in.
              </MessageBarBody>
            </MessageBar>
          )}
          <PlanBody data={current.data} />
          <OverlayDrawer
            open={railOpen}
            position="end"
            aria-label="Decisions"
            onOpenChange={(_, d) => setRailOpen(d.open)}
          >
            <DrawerHeader>
              <DrawerHeaderTitle
                action={
                  <Button
                    appearance="subtle"
                    icon={<Dismiss20Regular />}
                    aria-label="Close"
                    onClick={() => setRailOpen(false)}
                  />
                }
              >
                Decisions
              </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
              <DecisionRail
                plan={current.data.plan}
                state={current.data.state}
                onGo={(d) => {
                  setRailOpen(false)
                  // After the drawer has handed the focus back, or it takes it again.
                  setTimeout(() => spotlight(d.target, d.section), 300)
                }}
              />
            </DrawerBody>
          </OverlayDrawer>
        </>
      )}
    </div>
  )
}

/** Decisions, with how many are still open. */
function DecisionsButton({ data, onClick }: { data: PlanningCycle; onClick: () => void }) {
  const styles = useStyles()
  const { open } = decisionLog(data.plan, data.state)
  return (
    <Button icon={<Gavel20Regular />} onClick={onClick} data-testid="decisions-button">
      Decisions
      <Badge
        className={styles.count}
        appearance="tint"
        shape="rounded"
        color={open.length ? 'warning' : 'success'}
      >
        {open.length ? `${open.length} open` : 'No open decisions'}
      </Badge>
    </Button>
  )
}

/**
 * Which of the given sections is on screen: the one crossing a band a tenth of
 * the way down the viewport, just under the sticky agenda. Between sections the
 * last answer stands, and the first section is the answer until the page scrolls.
 */
function useSectionOnScreen(ids: string[]): string | undefined {
  const [here, setHere] = useState<string | undefined>(ids[0])
  const key = ids.join()
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const inBand = new Set<string>()
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) inBand.add(e.target.id)
          else inBand.delete(e.target.id)
        }
        const first = key.split(',').find((id) => inBand.has(id))
        if (first) setHere(first)
      },
      { rootMargin: '-10% 0px -85% 0px' },
    )
    for (const id of key.split(',')) {
      const el = document.getElementById(id)
      if (el) io.observe(el)
    }
    return () => io.disconnect()
  }, [key])
  return here
}

function PlanBody({ data }: { data: PlanningCycle }) {
  const styles = useStyles()
  const agenda = useAgendaStyles()
  const sections = usePlanSections(data)
  const here = useSectionOnScreen(sections.map((s) => `plan-${s.id}`))
  const barRef = useRef<HTMLElement>(null)

  return (
    <>
      <nav aria-label="Agenda" className={styles.agendaBar} ref={barRef}>
        <ol className={agenda.agenda}>
          {sections.map((s, i) => (
            <li key={s.id}>
              <a
                className={mergeClasses(agenda.link, here === `plan-${s.id}` && agenda.here)}
                aria-current={here === `plan-${s.id}` ? 'location' : undefined}
                href={`#plan-${s.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  const el = document.getElementById(`plan-${s.id}`)
                  if (el) {
                    // Land just below the bar, however many rows it has
                    // wrapped to; a fixed scroll margin can't know that.
                    const bar = barRef.current?.getBoundingClientRect().height ?? 0
                    const top = el.getBoundingClientRect().top + window.scrollY - bar - 12
                    window.scrollTo({ top, behavior: 'smooth' })
                  }
                  // The keyboard goes along to the section, instead of staying
                  // up in the agenda (#1578).
                  document.getElementById(`h-plan-${s.id}`)?.focus({ preventScroll: true })
                }}
              >
                <span className={agenda.num}>{i + 1}</span>
                {s.nav}
                {s.minutes != null && <Caption1>{s.minutes} min</Caption1>}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      {sections.map((s, i) => (
        <PlanSection
          key={s.id}
          id={`plan-${s.id}`}
          icon={s.icon}
          step={i + 1}
          title={s.title}
          minutes={s.minutes}
          hint={s.hint}
          draft={s.draft}
          extra={s.extra}
        >
          {s.body}
        </PlanSection>
      ))}
    </>
  )
}

export default AdminPlanning
